import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import './Groups.scss';
import { ReactComponent as Edit } from '../../../assets/edit-3.svg';
import { ReactComponent as Close } from '../../../assets/x.svg';
import AlertModal from '../../../components/AlertModal/AlertModal';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import PageTitle from '../../../components/PageTitle/PageTitle';
import Button from '../../../components/Button/Button';
import Table from '../../../components/Table/Table';
import { Modal } from '../../../components/Modal/Modal';
import { DynamicInput } from '../../../components/Inputs/Inputs';
import slugify from '../../../utils/slugify';

const emptyForm = {
  id: null,
  name: '',
  group_slug: '',
  description: '',
  website: '',
};

export default function Groups() {
  const { axiosAuth } = useContext(AuthContext);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState('create'); // "create" || "edit"
  const [form, setForm] = useState(emptyForm);
  const [autoSlug, setAutoSlug] = useState(true); // controla sync name -> slug

  // delete confirmation
  const [confirm, setConfirm] = useState({
    open: false,
    id: null,
  });

  // error modal
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: '',
  });

  // helpers
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: '', message: '' });
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth.get('/groups');
      setGroups(data || []);
    } catch (err) {
      openError(
        'Erro ao carregar grupos',
        err?.response?.data?.detail || err.message,
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, openError]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleAdd = useCallback(() => {
    setMode('create');
    setForm({ ...emptyForm });
    setAutoSlug(true); // novo grupo: sincroniza slug pelo nome
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((g) => {
    const next = {
      id: g.id,
      name: g.name || '',
      group_slug: g.group_slug || '',
      description: g.description || '',
      website: g.website || '',
    };
    setMode('edit');
    setForm(next);
    // ativa autoslug só se o o slug atual coincide com slugify(name)
    setAutoSlug((next.group_slug || '') === slugify(next.name || ''));
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((id) => {
    setConfirm({ open: true, id });
  }, []);

  const confirmDelete = useCallback(async () => {
    const id = confirm.id;
    if (!id) return;
    try {
      await axiosAuth.delete(`/groups/${id}`);
      await fetchGroups();
    } catch (err) {
      openError(
        'Erro ao apagar grupo',
        err?.response?.data?.detail || err.message,
      );
    } finally {
      setConfirm({ open: false, id: null });
    }
  }, [confirm, axiosAuth, fetchGroups, openError]);

  const updateForm = useCallback((name, value) => {
    setForm((p) => ({ ...p, [name]: value }));
  }, []);

  // atualiza name, mantendo o slug em sync se autoSlug for true
  const updateName = useCallback(
    (_, value) => {
      const nameVal = value || '';
      setForm((p) => {
        const next = { ...p, name: nameVal };
        if (autoSlug) next.group_slug = slugify(nameVal);
        return next;
      });
    },
    [autoSlug],
  );

  // atualiza slug, aplicando slugify e desligando autoSlug
  const updateSlug = useCallback((_, value) => {
    const s = slugify(value || '');
    setForm((p) => ({ ...p, group_slug: s }));
    setAutoSlug(false);
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      openError('Validação', 'O nome do grupo é obrigatório.');
      return;
    }

    // fallback: se o slug estiver vazio, gera a partir do nome
    const finalSlug = (form.group_slug || '').trim() || slugify(trimmedName);

    try {
      if (mode === 'create') {
        await axiosAuth.post('/groups/', {
          name: form.name.trim(),
          group_slug: finalSlug,
          description: form.description?.trim() || '',
          website: form.website?.trim() || '',
        });
      } else {
        await axiosAuth.put(`/groups/${form.id}`, {
          name: form.name.trim(),
          group_slug: finalSlug,
          description: form.description?.trim(),
          website: form.website?.trim(),
        });
      }
      await fetchGroups();
      setIsModalOpen(false);
    } catch (err) {
      openError(
        'Erro ao gravar grupo',
        err?.response?.data?.detail || err.message,
      );
    }
  }, [mode, form, axiosAuth, fetchGroups, openError]);

  // tabela
  const columns = useMemo(
    () => [
      { key: 'id', label: 'Id do grupo' },
      { key: 'name', label: 'Nome do grupo' },
      { key: 'description', label: 'Descrição' },
      { key: 'website', label: 'Website' },
      {
        key: 'actions',
        label: 'Ações',
        render: (g) => (
          <>
            <button
              className="action-button edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(g);
              }}
            >
              <Edit />
            </button>
            <button
              className="action-button delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(g.id);
              }}
            >
              <Close />
            </button>
          </>
        ),
      },
    ],
    [handleEdit, handleDelete],
  );

  const tableData = useMemo(
    () =>
      groups.map((g) => ({
        id: g.id,
        name: g.name,
        group_slug: g.group_slug ?? '',
        description: g.description ?? '',
        website: g.website ?? '',
      })),
    [groups],
  );

  return (
    <div className="groups-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Eliminar grupo?"
          variant="danger"
          message="Deseja eliminar este grupo?"
          secText="Fechar"
          varText="Remover"
          onCancel={() => setConfirm({ open: false, id: null })}
          onConfirm={confirmDelete}
        />
      )}

      <PageTitle link="" title="Grupos" />

      <div className="filters">
        <Button type="button" onClick={handleAdd} disabled={loading}>
          Novo grupo
        </Button>
      </div>

      <div className="container">
        <Table columns={columns} data={tableData} />
      </div>

      {isModalOpen && (
        <Modal
          isVisible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={mode === 'create' ? 'Novo grupo' : `Editar grupo ${form.name}`}
          onSave={handleSave}
          saveText="Guardar"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="row">
              <DynamicInput
                type="text"
                label="Nome:"
                name="name"
                value={form.name}
                onChange={updateName}
                required
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Slug"
                name="group_slug"
                value={form.group_slug}
                onChange={updateSlug}
                disabled
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Descrição:"
                name="description"
                value={form.description}
                onChange={updateForm}
              />
            </div>
            <div className="row">
              <DynamicInput
                type="text"
                label="Website:"
                name="website"
                value={form.website}
                onChange={updateForm}
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
